import type { AssetMetricCatalogEntry } from '@src/providers/CoinMetricsApiProvider';
import { useMemo } from 'react';

/**
 * `requiredMetrics` for which no entry in `catalogEntries` supports the metric at all (optionally
 * scoped to an exact `frequency`) — i.e. the org's CoinMetrics API key appears to have no catalog
 * access to that metric, for any asset. CoinMetrics' catalog typically omits a metric the key isn't
 * entitled to from every entry rather than erroring on the catalog call itself, so this has to be
 * detected empirically across the whole response rather than from a request error.
 *
 * Pass `frequency` when the metric must be available at one specific cadence (e.g. Asset
 * Leaderboard's daily-only requirement, where a metric offered only hourly still counts as
 * "missing"); omit it to check for the metric at any cadence (e.g. Funding Rate, which can run
 * hourly or daily and whose entitlement isn't frequency-specific).
 *
 * Deliberately does NOT special-case an empty `catalogEntries` as "nothing missing" — a genuinely
 * resolved, empty catalog for a single required metric (e.g. Funding Rate's one-metric asset map) is
 * exactly the "the key has zero access to it" case this function exists to catch, and is
 * indistinguishable from "the catalog just hasn't loaded yet" by looking at `catalogEntries` alone.
 * Callers MUST gate on their own catalog `isLoading` flag before trusting this result (e.g. skip
 * calling it, or discard the result, while the catalog request is still in flight) — otherwise every
 * required metric will read as missing for the split second before the first response lands.
 * Exported for unit testing.
 */
export function detectMetricsMissingFromCatalog(
  catalogEntries: readonly AssetMetricCatalogEntry[],
  requiredMetrics: readonly string[],
  frequency?: string
): string[] {
  return requiredMetrics.filter(
    requiredMetric =>
      !catalogEntries.some(entry =>
        entry.metrics.some(
          metricEntry =>
            metricEntry.metric === requiredMetric &&
            (frequency == null || metricEntry.frequencies.some(f => f.frequency === frequency))
        )
      )
  );
}

const EMPTY_MISSING_METRICS: string[] = [];

/**
 * Memoized `detectMetricsMissingFromCatalog`, gated on `isLoading` so callers don't each re-implement
 * the "loading vs. genuinely empty" guard documented above.
 */
export function useMissingMetrics(
  catalogEntries: readonly AssetMetricCatalogEntry[],
  requiredMetrics: readonly string[],
  isLoading: boolean,
  frequency?: string
): string[] {
  return useMemo(
    () =>
      isLoading ? EMPTY_MISSING_METRICS : detectMetricsMissingFromCatalog(catalogEntries, requiredMetrics, frequency),
    [catalogEntries, requiredMetrics, isLoading, frequency]
  );
}
